import React from "react";

/* ---------------- Resolution badges ---------------- */
// Granular pixel-resolution chip (4K / 1440p / 1080p / 720p / 480p / 360p)
// rendered alongside the legacy QualityBadge.

const RESOLUTION_STYLE = {
  "4K":    { bg: "linear-gradient(145deg, #f7d774 0%, #c98b18 50%, #f7d774 100%)", color: "#1a1100", sub: "ULTRAHD" },
  "1440p": { bg: "linear-gradient(180deg, #064e3b 0%, #10b981 100%)", color: "#fff" },
  "1080p": { bg: "linear-gradient(180deg, #0e3b66 0%, #1d4ed8 100%)", color: "#fff" },
  "720p":  { bg: "linear-gradient(180deg, #1a1a2e 0%, #4a5fd1 100%)", color: "#fff" },
  "480p":  { bg: "#3a3a3a", color: "#cfcfcf" },
  "360p":  { bg: "#2a2a2a", color: "#aaa" },
};

export function ResolutionBadge({ resolution }) {
  if (!resolution) return null;
  const style = RESOLUTION_STYLE[resolution];
  if (!style) return null;
  return (
    <span
      data-testid={`badge-res-${resolution.toLowerCase()}`}
      title={resolution === "4K" ? "4K · 2160p" : resolution}
      className="inline-flex flex-col items-center justify-center shrink-0 rounded-md font-display font-extrabold leading-none px-1.5 py-1 text-[10px]"
      style={{
        background: style.bg,
        color: style.color,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)",
      }}
    >
      <span className="leading-none">{resolution}</span>
      {style.sub && (
        <span className="leading-none text-[6px] tracking-wider mt-0.5">{style.sub}</span>
      )}
    </span>
  );
}

/* ---------------- Release source badges ---------------- */
// Where the file was ripped from (BluRay / REMUX / WEB-DL / …). Important for
// quality-conscious downloaders — a REMUX is byte-perfect to disc, a CAM is
// somebody filming a cinema screen.

const RELEASE_SOURCE_STYLE = {
  REMUX:   { bg: "linear-gradient(180deg, #4a0e0e 0%, #b91c1c 100%)", color: "#fff", tooltip: "REMUX · byte-perfect rip from disc" },
  BluRay:  { bg: "linear-gradient(180deg, #0c1a4a 0%, #1e3a8a 100%)", color: "#fff", tooltip: "BluRay rip" },
  "WEB-DL":{ bg: "linear-gradient(180deg, #3b0d6e 0%, #7c3aed 100%)", color: "#fff", tooltip: "WEB-DL · direct stream rip (lossless container)" },
  WEBRip:  { bg: "linear-gradient(180deg, #4c1d95 0%, #a855f7 100%)", color: "#fff", tooltip: "WEBRip · re-encoded from web stream" },
  HDTV:    { bg: "linear-gradient(180deg, #0f3a3a 0%, #14b8a6 100%)", color: "#fff", tooltip: "HDTV broadcast capture" },
  DVDRip:  { bg: "linear-gradient(180deg, #4a3608 0%, #b45309 100%)", color: "#fff", tooltip: "DVDRip" },
  CAM:     { bg: "linear-gradient(180deg, #6a0e0e 0%, #dc2626 100%)", color: "#fff", tooltip: "CAM · filmed in cinema (low quality)" },
};

export function ReleaseSourceBadge({ source }) {
  if (!source) return null;
  const style = RELEASE_SOURCE_STYLE[source];
  if (!style) return null;
  return (
    <span
      data-testid={`badge-release-${source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      title={style.tooltip}
      className="inline-flex items-center justify-center shrink-0 rounded-md font-display font-extrabold leading-none px-2 py-1 text-[10px] uppercase tracking-tight"
      style={{
        background: style.bg,
        color: style.color,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
      }}
    >
      {source}
    </span>
  );
}

/* ---------------- Audio badges ---------------- */
// Audio codec / surround layout. Multiple chips per torrent are possible
// (e.g. "Atmos" + "DTS-HD") so this renders a small inline row.

const AUDIO_STYLE = {
  Atmos:   { bg: "linear-gradient(180deg, #18181b 0%, #404040 100%)", color: "#fff", tooltip: "Dolby Atmos" },
  TrueHD:  { bg: "linear-gradient(180deg, #0c0c2e 0%, #1d4ed8 100%)", color: "#fff", tooltip: "Dolby TrueHD" },
  "DTS-HD":{ bg: "linear-gradient(180deg, #4a0e0e 0%, #b91c1c 100%)", color: "#fff", tooltip: "DTS-HD Master Audio" },
  "DTS-X": { bg: "linear-gradient(180deg, #6a0e0e 0%, #dc2626 100%)", color: "#fff", tooltip: "DTS:X" },
  DTS:     { bg: "linear-gradient(180deg, #4a1c1c 0%, #991b1b 100%)", color: "#fff", tooltip: "DTS" },
  "DDP5.1":{ bg: "linear-gradient(180deg, #1a1a3e 0%, #4338ca 100%)", color: "#fff", tooltip: "Dolby Digital Plus 5.1" },
  "DD5.1": { bg: "linear-gradient(180deg, #1a1a2e 0%, #4f46e5 100%)", color: "#fff", tooltip: "Dolby Digital 5.1" },
  DDP:     { bg: "linear-gradient(180deg, #1f1f3e 0%, #4338ca 100%)", color: "#fff", tooltip: "Dolby Digital Plus / EAC3" },
  DD:      { bg: "linear-gradient(180deg, #1a1a2e 0%, #3730a3 100%)", color: "#fff", tooltip: "Dolby Digital / AC3" },
  FLAC:    { bg: "linear-gradient(180deg, #0e3b1f 0%, #16a34a 100%)", color: "#fff", tooltip: "FLAC · lossless" },
  AAC:     { bg: "#1f1f1f", color: "#d4d4d4", tooltip: "AAC" },
  MP3:     { bg: "#1a1a1a", color: "#a3a3a3", tooltip: "MP3" },
  Opus:    { bg: "#202020", color: "#d4d4d4", tooltip: "Opus" },
};

function SingleAudioBadge({ label }) {
  const style = AUDIO_STYLE[label];
  if (!style) return null;
  return (
    <span
      data-testid={`badge-audio-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      title={style.tooltip}
      className="inline-flex items-center justify-center shrink-0 rounded-md font-mono-tt font-bold leading-none px-1.5 py-1 text-[10px]"
      style={{
        background: style.bg,
        color: style.color,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
      }}
    >
      {label}
    </span>
  );
}

export function AudioBadges({ audio }) {
  if (!audio || !audio.length) return null;
  return (
    <span className="inline-flex items-center gap-1 shrink-0">
      {audio.slice(0, 2).map((a) => (
        <SingleAudioBadge key={a} label={a} />
      ))}
    </span>
  );
}

/* ---------------- Quality badges ---------------- */

export function QualityBadge({ quality }) {
  if (!quality) return null;
  const q = quality.toUpperCase();
  if (q === "4K") {
    return (
      <span
        data-testid="badge-quality-4k"
        title="4K Ultra HD"
        className="inline-flex flex-col items-center justify-center shrink-0 rounded-md font-display font-extrabold leading-none px-1.5 py-1 text-[10px]"
        style={{
          background: "linear-gradient(145deg, #f7d774 0%, #c98b18 50%, #f7d774 100%)",
          color: "#1a1100",
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.4)",
        }}
      >
        <span className="leading-none">4K</span>
        <span className="leading-none text-[6px] tracking-wider mt-0.5">ULTRAHD</span>
      </span>
    );
  }
  if (q === "HD") {
    return (
      <span
        data-testid="badge-quality-hd"
        title="High Definition"
        className="inline-flex items-center justify-center shrink-0 rounded-md font-display font-extrabold leading-none px-2 py-1 text-[11px]"
        style={{ background: "#F9A826", color: "#000" }}
      >
        HD
      </span>
    );
  }
  return (
    <span
      data-testid="badge-quality-sd"
      title="Standard Definition"
      className="inline-flex items-center justify-center shrink-0 rounded-md font-display font-extrabold leading-none px-2 py-1 text-[11px]"
      style={{ background: "#3a3a3a", color: "#cfcfcf" }}
    >
      SD
    </span>
  );
}

/* ---------------- Codec badges ---------------- */

const CODEC_STYLE = {
  HEVC: { bg: "linear-gradient(180deg, #0a1830 0%, #1b3b73 100%)", color: "#fff", accent: "#3aa0ff" },
  "H.264": { bg: "linear-gradient(180deg, #1a1a1a 0%, #2c2c2c 100%)", color: "#fff", accent: "#7a7a7a" },
  AV1: { bg: "linear-gradient(180deg, #073d2c 0%, #117a55 100%)", color: "#fff", accent: "#22d39a" },
  XviD: { bg: "linear-gradient(180deg, #4a2a08 0%, #b56514 100%)", color: "#fff", accent: "#ffae42" },
  VP9: { bg: "linear-gradient(180deg, #2a0d4a 0%, #6d28d9 100%)", color: "#fff", accent: "#c4b5fd" },
};

export function CodecBadge({ codec }) {
  if (!codec || codec === "Other") return null;
  const style = CODEC_STYLE[codec];
  if (!style) {
    return (
      <span
        data-testid={`badge-codec-${codec.toLowerCase()}`}
        className="inline-flex items-center justify-center shrink-0 rounded-md font-mono-tt font-bold leading-none px-2 py-1 text-[10px] bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] border border-[var(--tt-border)]"
        title={codec}
      >
        {codec}
      </span>
    );
  }
  return (
    <span
      data-testid={`badge-codec-${codec.toLowerCase().replace(/\./g, "")}`}
      title={codec === "HEVC" ? "HEVC · High Efficiency Video Coding" : codec === "H.264" ? "H.264 / AVC" : codec}
      className="relative inline-flex items-center justify-center shrink-0 rounded-md font-display font-extrabold leading-none px-2 py-1 text-[10px] overflow-hidden"
      style={{
        background: style.bg,
        color: style.color,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
      }}
    >
      {codec === "HEVC" && (
        <svg viewBox="0 0 6 8" width="5" height="6" className="mr-1 shrink-0" aria-hidden="true">
          <polygon points="0,0 6,4 0,8" fill={style.accent} />
        </svg>
      )}
      <span className="tracking-tight">{codec}</span>
      <span
        aria-hidden="true"
        className="absolute right-0 top-0 w-2 h-2 opacity-60"
        style={{
          background: style.accent,
          clipPath: "polygon(100% 0, 0 0, 100% 100%)",
        }}
      />
    </span>
  );
}

/* ---------------- HDR / Dolby Vision badges ---------------- */

const HDR_STYLE = {
  "DV": { bg: "linear-gradient(180deg, #0b3a8a 0%, #1e5acc 100%)", color: "#fff", label: "DV", tooltip: "Dolby Vision" },
  "HDR10+": { bg: "linear-gradient(180deg, #4a1486 0%, #8b3df0 100%)", color: "#fff", label: "HDR10+", tooltip: "HDR10+" },
  "HDR10": { bg: "linear-gradient(180deg, #2a134a 0%, #6d28d9 100%)", color: "#fff", label: "HDR10", tooltip: "HDR10" },
  "HDR": { bg: "linear-gradient(180deg, #3b0f5e 0%, #9333ea 100%)", color: "#fff", label: "HDR", tooltip: "High Dynamic Range" },
};

export function HdrBadge({ hdr }) {
  if (!hdr) return null;
  const style = HDR_STYLE[hdr] || HDR_STYLE.HDR;
  return (
    <span
      data-testid={`badge-hdr-${(style.label || hdr).toLowerCase().replace(/\+/g, "plus")}`}
      title={style.tooltip}
      className="inline-flex items-center justify-center shrink-0 rounded-md font-display font-extrabold leading-none px-1.5 py-1 text-[10px]"
      style={{
        background: style.bg,
        color: style.color,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)",
      }}
    >
      {style.label}
    </span>
  );
}

/* ---------------- Language ---------------- */

const LANGUAGE_FLAGS = {
  English:    { flag: "🇬🇧", label: "ENG", code: "eng" },
  Russian:    { flag: "🇷🇺", label: "RUS", code: "rus" },
  French:     { flag: "🇫🇷", label: "FRE", code: "fre" },
  Spanish:    { flag: "🇪🇸", label: "SPA", code: "spa" },
  German:     { flag: "🇩🇪", label: "GER", code: "ger" },
  Italian:    { flag: "🇮🇹", label: "ITA", code: "ita" },
  Japanese:   { flag: "🇯🇵", label: "JPN", code: "jpn" },
  Korean:     { flag: "🇰🇷", label: "KOR", code: "kor" },
  Hindi:      { flag: "🇮🇳", label: "HIN", code: "hin" },
  Chinese:    { flag: "🇨🇳", label: "CHI", code: "chi" },
  Portuguese: { flag: "🇵🇹", label: "POR", code: "por" },
  Turkish:    { flag: "🇹🇷", label: "TUR", code: "tur" },
  Arabic:     { flag: "🇸🇦", label: "ARA", code: "ara" },
  Polish:     { flag: "🇵🇱", label: "POL", code: "pol" },
  Dutch:      { flag: "🇳🇱", label: "NLD", code: "nld" },
  Swedish:    { flag: "🇸🇪", label: "SWE", code: "swe" },
};

export function LanguageBadge({ language }) {
  if (!language) return null;
  if (language === "Multi") {
    return (
      <span
        data-testid="badge-lang-multi"
        title="Multi-language / Dual audio"
        className="inline-flex items-center gap-1 shrink-0 rounded-md font-display font-extrabold leading-none px-1.5 py-1 text-[10px]"
        style={{
          background: "linear-gradient(135deg, #FF2A6D 0%, #9D4CDD 50%, #22d3ee 100%)",
          color: "#fff",
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
        }}
      >
        🌐 MULTI
      </span>
    );
  }
  if (language === "Other" || language === "Unknown") {
    return (
      <span
        data-testid="badge-lang-other"
        title={language}
        className="inline-flex items-center gap-1 shrink-0 rounded-md font-mono-tt font-bold leading-none px-1.5 py-1 text-[9px] bg-zinc-800 text-zinc-200 border border-zinc-700"
      >
        🌐 {language === "Other" ? "OTH" : "?"}
      </span>
    );
  }
  const meta = LANGUAGE_FLAGS[language];
  if (!meta) return null;
  return (
    <span
      data-testid={`badge-lang-${meta.code}`}
      title={language}
      className="inline-flex items-center gap-1 shrink-0 rounded-md font-mono-tt font-bold leading-none px-1.5 py-1 text-[10px] bg-zinc-800 text-zinc-100 border border-zinc-700"
    >
      <span className="text-[12px] leading-none">{meta.flag}</span>
      <span>{meta.label}</span>
    </span>
  );
}

/* ---------------- Source badges (logo-style) ---------------- */

function ShipIcon() {
  return (
    <svg viewBox="0 0 20 20" width="11" height="11" aria-hidden="true" className="shrink-0">
      <path
        fill="currentColor"
        d="M10 1l2 3h-1v3l3 1V5h2v4l2 1-1 2h-1l-1 4H5L4 12H3l-1-2 2-1V5h2v3l3-1V4H8l2-3zM6 14h8l-1 3H7l-1-3z"
      />
    </svg>
  );
}

function ReelIcon() {
  return (
    <svg viewBox="0 0 20 20" width="11" height="11" aria-hidden="true" className="shrink-0">
      <circle cx="6" cy="6" r="2" fill="currentColor" opacity="0.95" />
      <circle cx="11" cy="5" r="1.4" fill="currentColor" opacity="0.95" />
      <circle cx="6" cy="11" r="1.4" fill="currentColor" opacity="0.95" />
      <path d="M3 14 Q10 20 17 14" stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  );
}

function TvIcon() {
  return (
    <svg viewBox="0 0 20 20" width="11" height="11" aria-hidden="true" className="shrink-0">
      <rect x="2" y="6" width="16" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M6 4l4 3 4-3" stroke="currentColor" strokeWidth="1.6" fill="none" />
    </svg>
  );
}

function GalaxyIcon() {
  return (
    <svg viewBox="0 0 20 20" width="11" height="11" aria-hidden="true" className="shrink-0">
      <circle cx="10" cy="10" r="1.6" fill="currentColor" />
      <path d="M10 3a7 7 0 0 1 0 14" stroke="currentColor" strokeWidth="1.4" fill="none" />
      <path d="M10 17a7 7 0 0 1 0-14" stroke="currentColor" strokeWidth="1.4" fill="none" opacity="0.55" />
    </svg>
  );
}

const SOURCE_STYLES = {
  YTS: {
    bg: "linear-gradient(180deg, #0a4d18 0%, #16a34a 100%)",
    color: "#fff", label: "YTS", icon: <ReelIcon />, tooltip: "YTS / YIFY",
  },
  EZTV: {
    bg: "linear-gradient(180deg, #0a3d6e 0%, #1c89df 100%)",
    color: "#fff", label: "EZTV", icon: <TvIcon />, tooltip: "EZTV",
  },
  Knaben: {
    bg: "linear-gradient(180deg, #3b0d6e 0%, #9d4cdd 100%)",
    color: "#fff", label: "Knaben", tooltip: "Knaben aggregator",
  },
  "1337x": {
    bg: "linear-gradient(180deg, #6a0d0d 0%, #e74c3c 100%)",
    color: "#fff", label: "1337x", tooltip: "1337x",
  },
  TorrentGalaxy: {
    bg: "linear-gradient(180deg, #1c0a45 0%, #6d28d9 100%)",
    color: "#fff", label: "TGx", icon: <GalaxyIcon />, tooltip: "TorrentGalaxy",
  },
  "ext.to": {
    bg: "linear-gradient(180deg, #0e3b66 0%, #4a90e2 100%)",
    color: "#fff", label: "ext.to", tooltip: "ext.to",
  },
  "The Pirate Bay": {
    bg: "linear-gradient(180deg, #2a1a08 0%, #6b4824 100%)",
    color: "#f3d6a8", label: "TPB", icon: <ShipIcon />, tooltip: "The Pirate Bay",
  },
  "RuTracker.org": {
    bg: "linear-gradient(180deg, #16730d 0%, #58c43a 100%)",
    color: "#fff", label: "ru·tracker", twoTone: true, tooltip: "RuTracker.org",
  },
  "Nyaa": {
    bg: "linear-gradient(180deg, #134e4a 0%, #06b6d4 100%)",
    color: "#fff", label: "Nyaa", tooltip: "Nyaa",
  },
  "LimeTorrents": {
    bg: "linear-gradient(180deg, #14532d 0%, #22c55e 100%)",
    color: "#fff", label: "Lime", tooltip: "LimeTorrents",
  },
};

function matchSource(name) {
  if (!name) return null;
  if (SOURCE_STYLES[name]) return { key: name, ...SOURCE_STYLES[name] };
  const lower = name.toLowerCase();
  for (const k of Object.keys(SOURCE_STYLES)) {
    if (lower.includes(k.toLowerCase())) return { key: k, ...SOURCE_STYLES[k] };
  }
  return null;
}

export function SourceBadge({ source }) {
  const style = matchSource(source);
  if (!style) {
    return (
      <span
        data-testid="badge-source-unknown"
        title={source}
        className="inline-flex items-center justify-center shrink-0 rounded-md font-mono-tt font-bold leading-none px-2 py-1 text-[10px] uppercase tracking-wider"
        style={{ background: "var(--tt-primary)", color: "#fff" }}
      >
        {(source || "?").slice(0, 12)}
      </span>
    );
  }
  return (
    <span
      data-testid={`badge-source-${style.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      title={style.tooltip || style.key}
      className="inline-flex items-center gap-1 shrink-0 rounded-md font-display font-extrabold leading-none px-2 py-1 text-[10px]"
      style={{
        background: style.bg,
        color: style.color,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)",
      }}
    >
      {style.icon}
      {style.twoTone ? (
        <span>
          <span style={{ color: "#ffb4b4" }}>ru</span>
          <span>tracker</span>
        </span>
      ) : (
        <span className="tracking-tight">{style.label}</span>
      )}
    </span>
  );
}
